import { useState, useRef, useEffect, memo ,useCallback} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useImageRegeneration } from './useImageRegeneration';
import ImageRegenPanel from './ImageRegenPanel';

import { setChatInProgress } from './chatState';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
const APP_AUTH_STORAGE_KEY = 'app_auth_session';
const DEFAULT_MESSAGE = 'I can help you design and build interactive lessons. Tell me the topic, level, objectives, and any must-have interactions.';

// Canonical chat-capable OpenAI model. Keep legacy aliases for stored prefs.
const OPENAI_MODEL_ID = process.env.REACT_APP_OPENAI_MODEL_ID || 'gpt-5.4';
const OPENAI_MODEL_LABEL = process.env.REACT_APP_OPENAI_MODEL_LABEL || 'GPT 5.4';
const LEGACY_OPENAI_MODELS = ['gpt5', 'openai', 'gpt-5.1', 'gpt-5.1-codex', 'gpt-5.1-codex-max', 'gpt-5.4-mini'];

const BYOK_KEYS = { google: 'appstorm_byok_google', openai: 'appstorm_byok_openai', anthropic: 'appstorm_byok_anthropic' };
const getStoredByokKeys = () => {
  if (typeof localStorage === 'undefined') return {};
  const g = localStorage.getItem(BYOK_KEYS.google);
  const o = localStorage.getItem(BYOK_KEYS.openai);
  const a = localStorage.getItem(BYOK_KEYS.anthropic);
  const out = {};
  if (g && g.trim()) out.google_key = g.trim();
  if (o && o.trim()) out.openai_key = o.trim();
  if (a && a.trim()) out.anthropic_key = a.trim();
  return out;
};

const getAuthSessionToken = () => {
  if (typeof localStorage === 'undefined') return '';
  try {
    // Try Auth0 token first
    const auth0Key = Object.keys(localStorage).find(k => 
      k.startsWith('@@auth0spajs@@') && k.includes('openid')
    );
    if (auth0Key) {
      const raw = localStorage.getItem(auth0Key);
      const parsed = JSON.parse(raw);
      const token = parsed?.body?.access_token;
      if (token) return token;
    }
    // Fallback to old session token
    const raw = localStorage.getItem(APP_AUTH_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.session_token === 'string' ? parsed.session_token.trim() : '';
  } catch {
    return '';
  }
};

const withAuthFetchOptions = (options = {}) => {
  const merged = { ...options, credentials: 'include' };
  const headers = { ...(options.headers || {}) };
  const token = getAuthSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  merged.headers = headers;
  return merged;
};

// Model preference storage (UI preference only)
const getModelKey = (template) => `chat_model_${template}`;

const normalizeModel = (model) => {
  if (!model) return OPENAI_MODEL_ID;
  const clean = (model || '').trim().toLowerCase();
  if (LEGACY_OPENAI_MODELS.includes(clean)) return OPENAI_MODEL_ID;
  return model.trim() || OPENAI_MODEL_ID;
};

const getModelPreference = (template) => {
  const key = getModelKey(template);
  const stored = localStorage.getItem(key);
  return normalizeModel(stored || 'gpt-5.6-luna');
};

const setModelPreference = (template, model) => {
  const key = getModelKey(template);
  localStorage.setItem(key, model);
};

// Load messages from backend API (per template; model is accepted for legacy migration only).
const loadMessagesFromStorage = async (template, model) => {
  try {
    const modelQuery = model ? `&model=${encodeURIComponent(model)}` : '';
    const response = await fetch(
      `${API_URL}/chat/history?template=${encodeURIComponent(template)}${modelQuery}`,
      withAuthFetchOptions()
    );

    if (!response.ok) {
      throw new Error(`Failed to load chat history: ${response.status}`);
    }

    const data = await response.json();

    if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
      console.log(`[ChatAdvisor] Found ${data.messages.length} stored messages for ${template}/${model}`);
      return { messages: data.messages, threadId: data.thread_id || null, loadFailed: false };
    }

    console.log(`[ChatAdvisor] No stored messages found for ${template}/${model}, starting fresh`);
    return {
      messages: [{ role: 'advisor', text: DEFAULT_MESSAGE }],
      threadId: null,
      loadFailed: false
    };
  } catch (err) {
    console.error('Error loading chat messages from backend:', err);
    return {
      messages: [{ role: 'advisor', text: DEFAULT_MESSAGE }],
      threadId: null,
      loadFailed: true
    };
  }
};

const stripImageDataForStorage = (msgs) => {
  if (!Array.isArray(msgs)) return msgs;
  return msgs.map((m) => {
    if (!m) return m;
    let out = { ...m };
    if (typeof out.image === 'string' && out.image.startsWith('data:')) {
      out = { ...out, image: null };
    }
    if (Array.isArray(out.images) && out.images.some((url) => typeof url === 'string' && url.startsWith('data:'))) {
      out = { ...out, images: [] };
    }
    return out;
  });
};

// Save messages to backend API (per template; model-independent). Strips image data URLs to keep payload small.
const saveMessagesToStorage = async (messages, template, threadId = null, options = {}) => {
  try {
    const payload = stripImageDataForStorage(messages);
    const response = await fetch(`${API_URL}/chat/history`, withAuthFetchOptions({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template,
        messages: payload,
        thread_id: threadId
      }),
    }));

    if (!response.ok) {
      throw new Error(`Failed to save chat history: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to save chat history');
    }

    console.log(`[ChatAdvisor] Saved ${messages.length} messages to backend for ${template}`);
  } catch (err) {
    console.error('Error saving chat history:', err);
  }
};

const getThreadId = async (template, model, existingThreadId = null) => {
  if (existingThreadId) {
    return existingThreadId;
  }
  return `thread_${template}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const isDefaultMessages = (msgs = []) => {
  return msgs.length === 1 &&
    msgs[0]?.role === 'advisor' &&
    typeof msgs[0]?.text === 'string' &&
    msgs[0].text.includes('I can help you design and build interactive lessons');
};

export const clearTemplateChatHistory = async (template) => {
  try {
    const response = await fetch(`${API_URL}/chat/history?template=${encodeURIComponent(template)}`, withAuthFetchOptions({
      method: 'DELETE',
    }));

    if (response.ok) {
      const data = await response.json();
      console.log(`[ChatAdvisor] Cleared chat history for ${template}:`, data.message || 'Success');
    } else {
      console.log(`[ChatAdvisor] No chat history found to clear for ${template} (already clean)`);
    }

    window.dispatchEvent(new CustomEvent('chatHistoryCleared', { detail: { template } }));
  } catch (err) {
    console.error('Error clearing chat history:', err);
  }
};

const modelToProvider = {
  gemini: 'google',
  'gemini-3.5-flash': 'google',
  'gpt-5.6-luna': 'openai',
  gpt5: 'openai',
  'gpt-5.1-codex': 'openai',
  'claude-sonnet-4-5': 'anthropic',
  'claude-opus-4-6': 'anthropic',
  'deepseek-v4-flash': 'deepseek',
  'deepseek-v4-pro': 'deepseek',
};
const isModelEnabledByConfig = (modelId, apiKeysConfig) => {
  if (!apiKeysConfig?.use_user_keys_only) return true;
  const provider = modelToProvider[modelId];
  return provider && apiKeysConfig.providers_set?.[provider];
};

function ChatAdvisor({
  selectedTemplate = 'template-1',
  isOwner = true,
  isCreatingTemplate = false,
  creatingTemplateDescription = '',
  pendingTemplateId = null,
  apiKeysConfig = { providers_set: {}, use_user_keys_only: false },
  defaultModelForTemplate = null,
  isEditMode = false,
  setIsEditMode = null,
  getAccessTokenSilently = null,
}) {
  const signalChatCycleComplete = (template) => {
    setChatInProgress(false);
    try {
      window.dispatchEvent(new CustomEvent('chatCycleComplete', { detail: { template } }));
    } catch (e) {
      // ignore dispatch errors
    }
  };

  const initialModel = defaultModelForTemplate && normalizeModel(defaultModelForTemplate) ? defaultModelForTemplate : getModelPreference(selectedTemplate);
  const [selectedModel, setSelectedModel] = useState(() => normalizeModel(initialModel));
  const currentContextRef = useRef({ template: selectedTemplate, model: normalizeModel(initialModel) });
  const loadFailuresRef = useRef({});

  const [messages, setMessages] = useState([{ role: 'advisor', text: DEFAULT_MESSAGE }]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const chatBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const csvExcelInputRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const threadIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const prevTemplateRef = useRef(null);
  const prevModelRef = useRef(selectedModel);
  const messagesRef = useRef(messages);
  const getTokenRef = useRef(getAccessTokenSilently);
  useEffect(() => { getTokenRef.current = getAccessTokenSilently; }, [getAccessTokenSilently]);

  const getFreshToken = async () => {
    try {
      if (getTokenRef.current) {
        return await getTokenRef.current({ ignoreCache: true });
      }
    } catch (e) {
      // silent refresh failed — fall back to cached token
    }
    return getAuthSessionToken();
  };
  const isSwitchingRef = useRef(false);
  const loadInProgressForRef = useRef(null); // dedupe template-change load (e.g. Strict Mode double run)

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Safety: avoid leaving the global chat lock set if this component unmounts mid-request.
      setChatInProgress(false);
    };
  }, []);

  useEffect(() => {
    if (!showAttachmentMenu) return;
    const handleClickOutside = (event) => {
      if (!attachmentMenuRef.current?.contains(event.target)) {
        setShowAttachmentMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachmentMenu]);

  const setLoadFailure = (template, failed) => {
    loadFailuresRef.current[template] = failed;
  };

  const shouldSkipDefaultSave = (msgs, template, options = {}) => {
    if (options.force) return false;
    const failed = !!loadFailuresRef.current[template];
    return failed && isDefaultMessages(msgs);
  };

  useEffect(() => {
    const loadInitialHistory = async () => {
      const model = getModelPreference(selectedTemplate);
      setIsLoadingHistory(true);
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, model);
        if (isMountedRef.current) {
          setMessages(result.messages);
          messagesRef.current = result.messages;
          threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, model);
          currentContextRef.current = { template: selectedTemplate, model };
          setLoadFailure(selectedTemplate, result.loadFailed);
          console.log(`[ChatAdvisor] Initial load for template: ${selectedTemplate}, model: ${model}, messages: ${result.messages.length}`);
        }
      } catch (err) {
        console.error('Error loading initial chat history:', err);
      } finally {
        if (isMountedRef.current) {
          setIsLoadingHistory(false);
        }
      }
    };
    if (!selectedTemplate) return;

    loadInitialHistory();
  }, [selectedTemplate]);

  useEffect(() => {
    const handleChatHistoryCleared = async (event) => {
      const clearedTemplate = event.detail?.template;
      if (clearedTemplate === selectedTemplate) {
        console.log(`[ChatAdvisor] Chat history cleared for ${selectedTemplate}, reloading...`);
        try {
          const result = await loadMessagesFromStorage(selectedTemplate, selectedModel);
          if (isMountedRef.current) {
            setMessages(result.messages);
            messagesRef.current = result.messages;
            threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, selectedModel);
            setLoadFailure(selectedTemplate, result.loadFailed);
          }
        } catch (err) {
          console.error('Error reloading chat history:', err);
        }
      }
    };

    window.addEventListener('chatHistoryCleared', handleChatHistoryCleared);
    return () => {
      window.removeEventListener('chatHistoryCleared', handleChatHistoryCleared);
    };
  }, [selectedTemplate, selectedModel]);

  // After template creation finishes, reload history (backend seeds it during creation)
  useEffect(() => {
    const handleRefresh = async (event) => {
      const refreshTemplate = event.detail?.template;
      if (refreshTemplate !== selectedTemplate) return;
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, selectedModel);
        if (isMountedRef.current) {
          setMessages(result.messages);
          messagesRef.current = result.messages;
          threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, selectedModel);
          setLoadFailure(selectedTemplate, result.loadFailed);
        }
      } catch (err) {
        console.error('Error refreshing chat history after creation:', err);
      }
    };
    window.addEventListener('chatHistoryRefresh', handleRefresh);
    return () => window.removeEventListener('chatHistoryRefresh', handleRefresh);
  }, [selectedTemplate, selectedModel]);

  // Listen for QA fix events and append them to chat history
  useEffect(() => {
    const handleQaFixApplied = async (event) => {
      const { template, userText, advisorText } = event.detail || {};
      if (!template) return;

      const userMsg = { role: 'user', text: userText };
      const advisorMsg = { role: 'advisor', text: advisorText };

      if (template === selectedTemplate) {
        // Fix is for the currently viewed template — update in-memory state and save
        const updated = [...messagesRef.current, userMsg, advisorMsg];
        setMessages(updated);
        messagesRef.current = updated;
        await saveMessagesToStorage(updated, template, threadIdRef.current);
      } else {
        // User switched to a different template — load that template's history from
        // storage, append the fix messages, and save back so they appear when the user
        // navigates back to that template.
        try {
          const stored = await loadMessagesFromStorage(template, null);
          const existing = stored?.messages || [];
          const updated = [...existing, userMsg, advisorMsg];
          await saveMessagesToStorage(updated, template, stored?.threadId || null);
        } catch (err) {
          console.error('[ChatAdvisor] Failed to persist QA fix to off-screen template:', err);
        }
      }
    };

    window.addEventListener('qaFixApplied', handleQaFixApplied);
    return () => {
      window.removeEventListener('qaFixApplied', handleQaFixApplied);
    };
  }, [selectedTemplate]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    const switchChatHistory = async () => {
      const isFirstLoad = prevTemplateRef.current === null;
      const templateChanged = prevTemplateRef.current !== selectedTemplate;
      if (!templateChanged) {
        return;
      }
      if (isFirstLoad) {
        prevTemplateRef.current = selectedTemplate;
        return;
      }

      if (loadInProgressForRef.current === selectedTemplate) return;
      loadInProgressForRef.current = selectedTemplate;
      isSwitchingRef.current = true;
      setIsLoadingHistory(true);

      const modelToUse = (defaultModelForTemplate && normalizeModel(defaultModelForTemplate)) || getModelPreference(selectedTemplate);
      if (modelToUse !== selectedModel) {
        setSelectedModel(modelToUse);
      }
      console.log(`[ChatAdvisor] Template changed: ${prevTemplateRef.current} -> ${selectedTemplate}`);
      
      if (prevTemplateRef.current && messagesRef.current.length > 0 && !isDefaultMessages(messagesRef.current)) {
        console.log(`[ChatAdvisor] Saving ${messagesRef.current.length} messages for ${prevTemplateRef.current}`);
        if (!shouldSkipDefaultSave(messagesRef.current, prevTemplateRef.current)) {
          await saveMessagesToStorage(messagesRef.current, prevTemplateRef.current, threadIdRef.current);
        }
      }

   
      setMessages([{ role: 'advisor', text: DEFAULT_MESSAGE }]);
      messagesRef.current = [{ role: 'advisor', text: DEFAULT_MESSAGE }];
      

      try {
        const result = await loadMessagesFromStorage(selectedTemplate, modelToUse);
        console.log(`[ChatAdvisor] Loading ${result.messages.length} messages for ${selectedTemplate}`);

        currentContextRef.current = { template: selectedTemplate, model: modelToUse };

        if (isMountedRef.current) {
          setMessages(result.messages);
          messagesRef.current = result.messages;
          threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, modelToUse);
          setLoadFailure(selectedTemplate, result.loadFailed);
          console.log(`[ChatAdvisor] Thread ID updated to: ${threadIdRef.current}`);
        }
      } catch (err) {
        console.error('Error loading chat history:', err);
      } finally {
        loadInProgressForRef.current = null;
        if (isMountedRef.current) {
          setIsLoadingHistory(false);
        }
      }

      setDraft('');
      setSelectedImages([]);
      setImagePreviews([]);
      setSelectedFile(null);
      setUploadedFilePath(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (csvExcelInputRef.current) {
        csvExcelInputRef.current.value = '';
      }

      prevTemplateRef.current = selectedTemplate;
      prevModelRef.current = modelToUse;

      setTimeout(() => {
        isSwitchingRef.current = false;
      }, 100);
    };

    switchChatHistory();
  }, [selectedTemplate, defaultModelForTemplate]);

  // ── Image regeneration ───────────────────────────────────────────────────
  const { regen, start: startRegen, retry: retryRegen, accept: acceptRegen, cancel: cancelRegen } =
    useImageRegeneration(selectedTemplate);

  // Track committed swaps so iframe can be patched without reload
  const [regenCommits, setRegenCommits] = useState({}); // { oldUrl: newUrl }

  // Called when user hovers an image in the chat messages and clicks regenerate
  const handleImageRegenClick = useCallback((src, alt, surroundingText) => {
    const cleanSrc = src.split('?')[0];
    startRegen(cleanSrc, alt, surroundingText);
  }, [startRegen]);

  const handleRegenAccept = useCallback(async () => {
    const committed = await acceptRegen();
    if (committed && regen?.originalUrl) {
      const oldClean = regen.originalUrl.split('?')[0];
      setRegenCommits(prev => ({ ...prev, [oldClean]: committed }));
    }
  }, [acceptRegen, regen]);

  const handleModelChange = (newModel) => {
    console.log(`[ChatAdvisor] Model change requested: ${selectedModel} -> ${newModel} for template: ${selectedTemplate}`);
    setSelectedModel(newModel);
    setModelPreference(selectedTemplate, newModel);
    currentContextRef.current = { ...currentContextRef.current, model: newModel };
    prevModelRef.current = newModel;
  };

  useEffect(() => {
    currentContextRef.current = { ...currentContextRef.current, model: selectedModel };
  }, [selectedModel]);

  const enabledModelIds = [
    'gpt-5.6-luna', 'gemini-3.5-flash', 'gemini', OPENAI_MODEL_ID, 'claude-sonnet-4-5', 'claude-opus-4-6', 'deepseek-v4-flash', 'deepseek-v4-pro',
    ...(process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' ? [
  'ollama-qwen3.5-397b', 'ollama-kimi-k2.5',
  'ollama-qwen3-coder-next', 'ollama-gemma4-31b'
] : [])
  ].filter((id) => isModelEnabledByConfig(id, apiKeysConfig));
  useEffect(() => {
    if (apiKeysConfig?.use_user_keys_only && enabledModelIds.length && !enabledModelIds.includes(selectedModel)) {
      const fallback = enabledModelIds[0];
      setSelectedModel(fallback);
      setModelPreference(selectedTemplate, fallback);
      currentContextRef.current = { ...currentContextRef.current, model: fallback };
      prevModelRef.current = fallback;
    }
  }, [apiKeysConfig?.use_user_keys_only, enabledModelIds.join(','), selectedModel, selectedTemplate]);

  useEffect(() => {
    if (isSwitchingRef.current || isLoadingHistory) {
      return;
    }

    if (currentContextRef.current.template !== selectedTemplate) {
      console.log(`[ChatAdvisor] Context mismatch during save, skipping. Current: ${currentContextRef.current.template}, Expected: ${selectedTemplate}`);
      return;
    }

    if (messages.length > 0 && !isDefaultMessages(messages)) {
      console.log(`[ChatAdvisor] Auto-saving ${messages.length} messages for ${selectedTemplate}`);
      saveMessagesToStorage(messages, selectedTemplate, threadIdRef.current);
    }
  }, [messages, selectedTemplate, isLoadingHistory]);

  useEffect(() => {
    return () => {
      if (messages.length > 0 && !isSwitchingRef.current && !isDefaultMessages(messages)) {
        saveMessagesToStorage(messages, selectedTemplate, threadIdRef.current);
      }
    };
  }, [messages, selectedTemplate]);

  useEffect(() => {
    if (isSwitchingRef.current || isLoadingHistory || isLoading) {
      return;
    }

    if (currentContextRef.current.template !== selectedTemplate) {
      return;
    }

    if (messages.length > 1 || !isDefaultMessages(messages)) {
      return;
    }

    const checkBackendHistory = async () => {
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, selectedModel);
        const isCorrectContext = currentContextRef.current.template === selectedTemplate &&
                                 prevTemplateRef.current === selectedTemplate;

        if (result.messages.length > messages.length && isCorrectContext) {
          console.log(`[ChatAdvisor] Restoring chat from backend. Backend has: ${result.messages.length}, Current has: ${messages.length}`);
          if (isMountedRef.current) {
            setMessages(result.messages);
            messagesRef.current = result.messages;
            if (result.threadId) {
              threadIdRef.current = result.threadId;
            }
          }
        }
      } catch (e) {
        console.error('Error checking backend history:', e);
      }
    };

    checkBackendHistory();
  }, [messages.length, selectedTemplate, selectedModel, isLoadingHistory, isLoading]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [draft]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const MAX_IMAGES = 6;
  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  const handleImageSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files (PNG/JPG/etc.)');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image ${file.name} must be less than 10MB`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;
    const toAdd = valid.slice(0, MAX_IMAGES - selectedImages.length);
    if (toAdd.length === 0) {
      alert(`You can attach up to ${MAX_IMAGES} images.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles = [...selectedImages, ...toAdd].slice(0, MAX_IMAGES);
    setSelectedImages(newFiles);
    try {
      const newPreviews = await Promise.all(toAdd.map(readFileAsDataURL));
      setImagePreviews((prev) => [...prev, ...newPreviews].slice(0, MAX_IMAGES));
    } catch (err) {
      console.error('Error reading images:', err);
      setSelectedImages(selectedImages);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls', 'pdf', 'docx', 'pptx'].includes(fileExt)) {
        alert('Please select a supported file (.csv, .xlsx, .xls, .pdf, .docx, .pptx)');
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }

      setSelectedFile(file);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (data.success) {
          setUploadedFilePath(data.file_path);
          console.log(`File uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setSelectedFile(null);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Failed to upload file. Please try again.');
        setSelectedFile(null);
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadedFilePath(null);
    if (csvExcelInputRef.current) {
      csvExcelInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    if (index !== undefined) {
      setSelectedImages((prev) => prev.filter((_, i) => i !== index));
      setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    } else {
      setSelectedImages([]);
      setImagePreviews([]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const sendMessage = async () => {
    if ((!draft.trim() && selectedImages.length === 0 && !uploadedFilePath) || isLoading) return;

    if (!isOwner) {
      alert('You do not have permission to edit this lesson.');
      return;
    }

    if (isSwitchingRef.current) {
      console.log('[ChatAdvisor] Cannot send message during template/model switch');
      return;
    }

    if (currentContextRef.current.template !== selectedTemplate ||
        currentContextRef.current.model !== selectedModel) {
      const templateMismatch = currentContextRef.current.template !== selectedTemplate;
      const modelMismatch = currentContextRef.current.model !== selectedModel;
      console.log(
        `[ChatAdvisor] Context mismatch before send. Current: ${currentContextRef.current.template}/${currentContextRef.current.model}, ` +
        `Expected: ${selectedTemplate}/${selectedModel} (templateMismatch=${templateMismatch}, modelMismatch=${modelMismatch})`
      );

      // Model switches can race with send clicks; self-heal when only the model changed.
      if (!templateMismatch && modelMismatch) {
        currentContextRef.current = { ...currentContextRef.current, model: selectedModel };
      } else {
        return;
      }
    }

    const imagesToSend = [...selectedImages];
    const previewsToShow = [...imagePreviews];
    const filePathToSend = uploadedFilePath;
    const draftMessage = draft.trim();
    const userMessage = draftMessage || (imagesToSend.length ? `Describe these ${imagesToSend.length} image(s)` : uploadedFilePath ? 'Analyze this file' : '');
    const userMsgObj = {
      role: 'user',
      text: userMessage,
      image: previewsToShow.length === 1 ? previewsToShow[0] : null,
      images: previewsToShow.length > 1 ? previewsToShow : null,
      file: filePathToSend ? `File: ${selectedFile?.name || 'File'}` : null
    };

    setChatInProgress(true);

    const messageContext = { template: selectedTemplate, model: selectedModel };

    const baseMessages = messagesRef.current || [];
    const messagesWithUser = [...baseMessages, userMsgObj];
    setMessages(messagesWithUser);
    messagesRef.current = messagesWithUser;

    setDraft('');
    removeImage();
    setSelectedImages([]);
    setImagePreviews([]);
    removeFile();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    if (!shouldSkipDefaultSave(messagesWithUser, messageContext.template)) {
      saveMessagesToStorage(messagesWithUser, messageContext.template, threadIdRef.current).catch((err) => {
        console.error('[ChatAdvisor] Background save failed:', err);
      });
    }

    try {
      const imagesPayload = [];
      for (let i = 0; i < imagesToSend.length; i++) {
        const b64 = await convertImageToBase64(imagesToSend[i]);
        imagesPayload.push({ base64: b64, mime_type: imagesToSend[i].type || 'image/png' });
      }

      const requestBody = {
        message: userMessage,
        thread_id: threadIdRef.current,
        template: messageContext.template,
        model: messageContext.model
      };
      Object.assign(requestBody, getStoredByokKeys());

      if (imagesPayload.length === 1) {
        requestBody.image_base64 = imagesPayload[0].base64;
        requestBody.image_mime_type = imagesPayload[0].mime_type;
      } else if (imagesPayload.length > 1) {
        requestBody.images = imagesPayload;
      }

      if (filePathToSend) {
        requestBody.uploaded_file_path = filePathToSend;
      }

      const makeChatRequest = async (token) => {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return fetch(`${API_URL}/chat/stream`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify(requestBody),
        });
      };

      let token = await getFreshToken();
      let response = await makeChatRequest(token);

      if (response.status === 401) {
        // Token may have just expired — try one silent refresh and retry
        try {
          if (getTokenRef.current) {
            token = await getTokenRef.current({ ignoreCache: true });
            response = await makeChatRequest(token);
          }
        } catch (e) {
          // silent refresh failed
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        let msg = `Failed to get response from server (${response.status})`;
        try {
          const errData = await response.json();
          msg = errData?.error || errData?.detail || errData?.message || msg;
        } catch {
          // ignore parse errors
        }
        throw new Error(msg);
      }

      // Read SSE stream until the 'final' event
      const { text: finalText, streamData } = await (async () => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let eventType = '';
        let result = '';
        let parsedData = {};
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const parts = buf.split('\n');
            buf = parts.pop();
            for (const line of parts) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7).trim();
              } else if (line.startsWith('data: ') && eventType === 'final') {
                try { parsedData = JSON.parse(line.slice(6)) || {}; result = parsedData?.text || ''; } catch { /* ignore */ }
              } else if (line === '') {
                eventType = '';
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
        return { text: result, streamData: parsedData };
      })();
      console.log('AI Response received (stream):', finalText?.substring(0, 100));

      if (messageContext.template !== selectedTemplate) {
        console.log(`[ChatAdvisor] Template changed during async operation, discarding response. Original: ${messageContext.template}, Current: ${selectedTemplate}`);
        signalChatCycleComplete(messageContext.template);
        return;
      }

      const advisorMessages = [];
      if (finalText) {
        advisorMessages.push({ role: 'advisor', text: finalText });
      }
      if (streamData.evaluator_report) {
        advisorMessages.push({
          role: 'advisor',
          text: `Evaluator report:\n\`\`\`json\n${streamData.evaluator_report}\n\`\`\``
        });
      }
      if (advisorMessages.length === 0) {
        advisorMessages.push({ role: 'advisor', text: '' });
      }

      const afterAiMessages = [...messagesRef.current, ...advisorMessages];
      console.log('AI Response: Saving to backend, total messages:', afterAiMessages.length);
      setMessages(afterAiMessages);
      messagesRef.current = afterAiMessages;
      if (!shouldSkipDefaultSave(afterAiMessages, messageContext.template)) {
        await saveMessagesToStorage(afterAiMessages, messageContext.template, threadIdRef.current);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (isMountedRef.current) {
              setChatInProgress(false);
              try {
                window.dispatchEvent(new CustomEvent('chatCycleComplete', { detail: { template: messageContext.template } }));
              } catch (e) {
                // ignore dispatch errors
              }
              console.log('Chat completed, iframe reloads now allowed');
            }
          }, 3000);
        });
      });
    } catch (error) {
      console.error('Error sending message:', error);
      const isSessionExpired = error?.message === 'SESSION_EXPIRED';
      const errorMessage = {
        role: 'advisor',
        text: isSessionExpired
          ? 'Your session has expired. Please refresh the page and log in again to continue.'
          : 'Sorry, I encountered an error. Please try again.'
      };

      setMessages((prev) => {
        if (messageContext.template !== selectedTemplate) {
          console.log('[ChatAdvisor] Context changed during error handling, skipping save');
          return prev;
        }

        const updated = [...prev, errorMessage];
        if (!shouldSkipDefaultSave(updated, messageContext.template)) {
          saveMessagesToStorage(updated, messageContext.template, threadIdRef.current);
        }

        setTimeout(() => {
          signalChatCycleComplete(messageContext.template);
        }, 1000);

        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const LoadingDots = () => (
    <div className="loading-dots">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );

  const clearChat = async () => {
    if (window.confirm('Are you sure you want to clear the chat history for this template?')) {
      try {
        await fetch(`${API_URL}/chat/history?template=${encodeURIComponent(selectedTemplate)}`, {
          method: 'DELETE',
        });
      } catch (err) {
        console.error('Error deleting chat history:', err);
      }
      const defaultMessage = [{ role: 'advisor', text: DEFAULT_MESSAGE }];
      setMessages(defaultMessage);
      messagesRef.current = defaultMessage;
      threadIdRef.current = await getThreadId(selectedTemplate, selectedModel);
      await saveMessagesToStorage(defaultMessage, selectedTemplate, threadIdRef.current, { force: true });
    }
  };

  return (
    <div className="chat-panel">
      <div className="chat-body" ref={chatBodyRef}>
        {!isOwner && (
          <div className="chat-bubble advisor" style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            color: '#92400e',
            padding: '16px',
            marginBottom: '12px'
          }}>
            <strong>Lock: This lesson is owned by another user.</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
              You can view the lesson and chat history, but you cannot make edits.
            </p>
          </div>
        )}
        {(function() {
          const isCreateInProgress = isCreatingTemplate && selectedTemplate === pendingTemplateId && creatingTemplateDescription;
          if (isCreateInProgress) {
            return (
              <>
                <div className="chat-bubble user">
                  <span>{creatingTemplateDescription}</span>
                </div>
                <div className="chat-bubble advisor loading-bubble">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LoadingDots />
                    <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Generating your lesson...</span>
                  </div>
                </div>
              </>
            );
          }
          if (isLoadingHistory) {
            return (
              <div className="chat-loading-state" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                color: 'var(--muted)',
                fontSize: '14px',
                gap: '12px'
              }}>
                <LoadingDots />
                <span>Loading chat...</span>
              </div>
            );
          }
          const displayMessages = messages;
          const showLoadingBubble = isLoading;
          return (
            <>
              {displayMessages.map((m, idx) => (
                <div key={idx} className={`chat-bubble ${m.role}`}>
                  {m.image && (
                    <img src={m.image} alt="User uploaded" className="chat-message-image" />
                  )}
                  {m.images && m.images.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      {m.images.map((src, i) => (
                        <img key={i} src={src} alt={`Upload ${i + 1}`} className="chat-message-image" style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'cover', borderRadius: '6px' }} />
                      ))}
                    </div>
                  )}
                  {m.file && (
                    <div style={{
                      padding: '8px 12px',
                      marginBottom: '8px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: 'var(--text-primary)'
                    }}>
                      {m.file}
                    </div>
                  )}
                  {m.role === 'advisor' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                  ) : (
                    <span>{m.text}</span>
                  )}
                </div>
              ))}
              {showLoadingBubble && (
                <div className="chat-bubble advisor loading-bubble">
                  <LoadingDots />
                </div>
              )}
            </>
          );
        })()}
      </div>
      <div className="chat-input-container">
        {imagePreviews.length > 0 && (
          <div className="image-preview-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            {imagePreviews.map((src, idx) => (
              <div key={idx} className="image-preview-container" style={{ position: 'relative' }}>
                <img src={src} alt={`Preview ${idx + 1}`} className="image-preview" style={{ maxWidth: '72px', maxHeight: '72px', objectFit: 'cover', borderRadius: '8px' }} />
                <button type="button" onClick={() => removeImage(idx)} className="remove-image-button chat-icon-button" title="Remove image" style={{ position: 'absolute', top: '4px', right: '4px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span className="sr-only">Remove image</span>
                </button>
              </div>
            ))}
          </div>
        )}
        {selectedFile && (
          <div className="file-preview-container" style={{
            padding: '8px 12px',
            margin: '8px 0',
            background: 'var(--bg-secondary)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              File: {selectedFile.name}
            </span>
            <button
              onClick={removeFile}
              className="remove-image-button chat-icon-button"
              title="Remove file"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span className="sr-only">Remove file</span>
            </button>
          </div>
        )}
        <div className="chat-input-row">
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImageSelect}
            disabled={isLoading}
          />
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.pdf,.docx,.pptx"
            ref={csvExcelInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={isLoading}
          />
          <div className="attachment-menu-wrapper" ref={attachmentMenuRef}>
            <button
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className="attachment-button chat-icon-button"
              title="Add attachment"
              disabled={isLoading || !isOwner}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 1 1 4.95 4.95l-8.49 8.49a2 2 0 1 1-2.83-2.83l7.78-7.78"></path>
              </svg>
              <span className="sr-only">Add attachment</span>
            </button>
            {showAttachmentMenu && (
              <div className="attachment-menu">
                <button
                  type="button"
                  className="attachment-menu-item"
                  onClick={() => {
                    setShowAttachmentMenu(false);
                    fileInputRef.current?.click();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>Image (PNG/JPG)</span>
                </button>
                <button
                  type="button"
                  className="attachment-menu-item"
                  onClick={() => {
                    setShowAttachmentMenu(false);
                    csvExcelInputRef.current?.click();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="8" y1="13" x2="16" y2="13"></line>
                    <line x1="8" y1="17" x2="16" y2="17"></line>
                  </svg>
                  <span>Files (CSV/Excel/PDF/DOCX/PPTX)</span>
                </button>
              </div>
            )}
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isOwner ? 'Ask a question or leave a note...' : 'Lesson is read-only.'}
            disabled={isLoading || !isOwner}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && isOwner) {
                e.preventDefault();
                sendMessage();
              }
            }}
            rows={1}
            className="chat-textarea"
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !isOwner || (!draft.trim() && selectedImages.length === 0 && !uploadedFilePath)}
            className="chat-send-button"
            title={isOwner ? 'Send message (Enter)' : 'Lesson is read-only'}
          >
            {isLoading ? (
              <span className="button-loading-dots">
                <span></span><span></span><span></span>
              </span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            )}
            <span className="sr-only">Send message</span>
          </button>
        </div>
        <div className="chat-model-row">
          <label className="chat-model-label" htmlFor="chat-model-select">Model</label>
          <select
            id="chat-model-select"
            value={enabledModelIds.includes(selectedModel) ? selectedModel : (enabledModelIds[0] || OPENAI_MODEL_ID)}
            onChange={(e) => handleModelChange(e.target.value)}
            className="model-selector"
            title="Select AI Model"
            disabled={isLoading || !isOwner}
          >
            <option value="gpt-5.6-luna" disabled={!isModelEnabledByConfig('gpt-5.6-luna', apiKeysConfig)}>GPT 5.6 Luna</option>
            <option value="gemini-3.5-flash" disabled={!isModelEnabledByConfig('gemini-3.5-flash', apiKeysConfig)}>Google Gemini 3.5 Flash</option>
            <option value={OPENAI_MODEL_ID} disabled={!isModelEnabledByConfig(OPENAI_MODEL_ID, apiKeysConfig)}>{OPENAI_MODEL_LABEL}</option>
            <option value="claude-sonnet-4-5" disabled={!isModelEnabledByConfig('claude-sonnet-4-5', apiKeysConfig)}>Claude Sonnet 4.5</option>
            <option value="claude-opus-4-6" disabled={!isModelEnabledByConfig('claude-opus-4-6', apiKeysConfig)}>Claude Opus 4.6</option>
            <option value="deepseek-v4-flash" disabled={!isModelEnabledByConfig('deepseek-v4-flash', apiKeysConfig)}>DeepSeek V4 Flash</option>
            <option value="deepseek-v4-pro" disabled={!isModelEnabledByConfig('deepseek-v4-pro', apiKeysConfig)}>DeepSeek V4 Pro</option>
            {process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' && <>
  <option value="ollama-qwen3.5-397b" disabled={!isModelEnabledByConfig('ollama-qwen3.5-397b', apiKeysConfig)}>Ollama: Qwen 3.5 397B</option>
  <option value="ollama-kimi-k2.5" disabled={!isModelEnabledByConfig('ollama-kimi-k2.5', apiKeysConfig)}>Ollama: Kimi K2.5</option>
  <option value="ollama-qwen3-coder-next" disabled={!isModelEnabledByConfig('ollama-qwen3-coder-next', apiKeysConfig)}>Ollama: Qwen3 Coder Next</option>
  <option value="ollama-gemma4-31b" disabled={!isModelEnabledByConfig('ollama-gemma4-31b', apiKeysConfig)}>Ollama: Gemma4 31B</option>
</>}
          </select>
          {setIsEditMode && isOwner && (
  <button
    onClick={() => setIsEditMode?.(!isEditMode)}
    className="edit-content-button chat-icon-button"
    title={isEditMode ? 'Close inline editor' : 'Open inline editor'}
    style={{
      backgroundColor: isEditMode ? '#10b981' : 'transparent',
      color: isEditMode ? '#fff' : 'currentColor',
      padding: '6px 10px',
      borderRadius: '4px',
      border: isEditMode ? 'none' : '1px solid var(--border)',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
    <span className="sr-only">{isEditMode ? 'Close' : 'Open'} inline editor</span>
  </button>
)}
          <button
            onClick={clearChat}
            className="clear-chat-button chat-icon-button"
            title="Clear chat history"
            disabled={!isOwner}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
              <path d="M10 11v6"></path>
              <path d="M14 11v6"></path>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
            </svg>
            <span className="sr-only">Clear chat</span>
          </button>
        </div>
      </div>
      <ImageRegenPanel
        regen={regen}
        onAccept={handleRegenAccept}
        onCancel={cancelRegen}
        onRetry={retryRegen}
      />
    </div>
  );
}

export default memo(ChatAdvisor);
